import { Fragment, type ReactNode } from "react";
import type { Spans } from "../api";
import { utf8Length, withSpeakers, type Speakers } from "./speakers";

/**
 * ParagraphGap is the break that would draw a blank line between two
 * paragraphs, drawn as `.paragraph-gap` instead: a block a little shorter than
 * a line of text. The break is still in it, so the text copies and counts as
 * written; only its height changes. It must hold the break that *ends* the
 * blank line: the one before it ends the paragraph's last line, and a break at
 * the end of a block adds no line of its own.
 */
export function ParagraphGap() {
  return <span className="paragraph-gap">{"\n"}</span>;
}

/**
 * withGaps is withSpeakers over text that may hold paragraph breaks: every
 * break that ends a blank line — one following another, with only spaces
 * between, which a pre-line block collapses — is a ParagraphGap. `start` is
 * where the text starts in the chapter, in bytes.
 */
export function withGaps(
  text: string,
  start: number,
  speakers: Speakers | null | undefined,
  emphasis?: Spans,
): ReactNode {
  const gaps = [...text.matchAll(/\n[^\S\n]*(?=\n)/g)].map((m) => m.index + m[0].length);
  if (gaps.length === 0) return withSpeakers(text, start, speakers, emphasis);
  const pieces: ReactNode[] = [];
  let from = 0;
  for (const at of gaps) {
    if (at > from) {
      pieces.push(
        <Fragment key={from}>
          {withSpeakers(
            text.slice(from, at),
            start + utf8Length(text.slice(0, from)),
            speakers,
            emphasis,
          )}
        </Fragment>,
      );
    }
    pieces.push(<ParagraphGap key={`gap-${at}`} />);
    from = at + 1;
  }
  if (from < text.length) {
    pieces.push(
      <Fragment key={from}>
        {withSpeakers(
          text.slice(from),
          start + utf8Length(text.slice(0, from)),
          speakers,
          emphasis,
        )}
      </Fragment>,
    );
  }
  return <>{pieces}</>;
}
